import React, { useEffect, useState } from "react";
import { Button, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  fetchQuizzes,
  resetCardQuizState,
  resetVideoQuizState,
  setSelectedQuiz,
  submitCardQuiz,
} from "../store/reducers/quizzesSlice";
import { getOrganizationById } from "../store/reducers/organizationSlice";
import { submitVideoQuiz } from "../store/reducers/quizzesSlice";

import { useHistory } from "react-router-dom";
import _ from "lodash";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaClipboardList,
  FaListAlt,
  FaPlay,
  FaQuestionCircle,
  FaSearch,
} from "react-icons/fa";

const QUIZ_TONES = {
  cards: {
    label: "First Aid",
    card: "border-[#8CDCA0] bg-[#C7F3D2]",
    top: "bg-[#25C456]",
    text: "text-[#1F9D4A]",
    meta: "text-[#3F5D6B]",
  },
  educational: {
    label: "First Aid",
    card: "border-[#89B9E8] bg-[#AFD3F4]",
    top: "bg-[#3F8FE5]",
    text: "text-[#2B83D8]",
    meta: "text-[#3F5D6B]",
  },
  organization: {
    label: "First Aid",
    card: "border-[#E8A5A5] bg-[#FFD3D3]",
    top: "bg-[#FF5A5F]",
    text: "text-[#555F71]",
    meta: "text-[#3F5D6B]",
  },
};

const getQuizTone = (quiz) => {
  const label = `${quiz?.displayName || ""} ${
    quiz?.displayName_dk || ""
  }`.toLowerCase();

  if (quiz?.category === "educational" || quiz?.video_id) {
    return QUIZ_TONES.educational;
  }

  if (
    quiz?.category === "cards" ||
    quiz?.card_id ||
    label.includes("prevention")
  ) {
    return QUIZ_TONES.cards;
  }

  return QUIZ_TONES.organization;
};

const QuizList = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { loading, data, totals } = useSelector(
    (state) => state.quizzes.quizzes,
  );
  const { videoQuiz, cardQuiz } = useSelector((state) => state.quizzes);
  const { organizationDetails } = useSelector((state) => state.organization);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [selectedQuiz, setSelectedQuizLocal] = useState(null);
  const [activeQuizId, setActiveQuizId] = useState(null);

  useEffect(() => {
    if (!organizationDetails?.id) {
      const orgId = localStorage.getItem("organisation_id");
      if (orgId) {
        dispatch(getOrganizationById(orgId));
      }
    }
  }, [organizationDetails, dispatch]);

  useEffect(() => {
    if (organizationDetails?.id) {
      dispatch(
        fetchQuizzes({
          orgId: organizationDetails.id,
          search: debouncedSearch,
        }),
      );
    }
  }, [debouncedSearch, dispatch, organizationDetails?.id]);

  useEffect(() => {
    const handler = _.debounce((value) => {
      setDebouncedSearch(value);
    }, 500);

    handler(search);
    return () => handler.cancel();
  }, [search]);

  const searchedData = data.filter((quiz) => {
    if (!debouncedSearch) return true;

    const searchValue = debouncedSearch.toLowerCase();

    return [quiz.displayName, quiz.displayName_dk, quiz.categoryLabel]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(searchValue));
  });

  const filteredData =
    activeFilter === "all"
      ? searchedData
      : searchedData.filter((quiz) => quiz.category === activeFilter);

  const getErrorMessage = (error, fallbackMessage) =>
    error?.error || error?.message || error || fallbackMessage;

  const closeModal = () => {
    if (videoQuiz.loading || cardQuiz.loading) {
      return;
    }

    setShowModal(false);
    setSelectedQuizLocal(null);
    dispatch(resetVideoQuizState());
    dispatch(resetCardQuizState());
  };

  const handleStart = async (quiz) => {
    if (!quiz) return;

    if (quiz.attempt !== 0) {
      dispatch(resetVideoQuizState());
      dispatch(resetCardQuizState());
      setSelectedQuizLocal(quiz);
      setShowModal(true);
      return;
    }

    try {
      setActiveQuizId(quiz.id);
      let hasValidId = false;

      let attemptPayload = {
        organization_id: organizationDetails?.id,
      };

      if (quiz?.video_id) {
        attemptPayload.video_id = quiz.video_id;
        hasValidId = true;
      } else if (quiz?.card_id) {
        attemptPayload.card_id = quiz.card_id;
        hasValidId = true;
      }

      dispatch(setSelectedQuiz(quiz));
      history.push(`/quizzes/${quiz.id}`);
    } catch (err) {
      toast.error("Unable to start quiz right now");
    } finally {
      setActiveQuizId(null);
    }
  };

  const handleYes = async () => {
    if (!selectedQuiz) return;

    try {
      setActiveQuizId(selectedQuiz.id);
      dispatch(resetVideoQuizState());
      dispatch(resetCardQuizState());

      let payload = {
        organization_id: organizationDetails?.id,
      };

      let action;

      if (selectedQuiz?.video_id) {
        payload.video_id = selectedQuiz.video_id;
        payload.want_video_quiz = true;
        action = submitVideoQuiz(payload);
      } else {
        payload.card_id = selectedQuiz.card_id;
        payload.want_card_quiz = true;
        action = submitCardQuiz(payload);
      }

      const res = await dispatch(action);

      if (
        submitVideoQuiz.rejected.match(res) ||
        submitCardQuiz.rejected.match(res)
      ) {
        toast.error(
          getErrorMessage(res.payload, "Unable to submit quiz request"),
        );
        return;
      }

      dispatch(setSelectedQuiz(selectedQuiz));

      await dispatch(fetchQuizzes(organizationDetails.id));

      setShowModal(false);
      setSelectedQuizLocal(null);

      history.push(`/quizzes/${selectedQuiz.id}`);
    } catch (err) {
      toast.error("Unable to submit quiz request");
    } finally {
      setActiveQuizId(null);
    }
  };

  const isModalLoading = videoQuiz.loading || cardQuiz.loading;
  const totalQuestions =
    totals?.total_questions ??
    data.reduce((total, quiz) => total + (quiz.questions?.length || 0), 0);
  const noExpiryCount =
    totals?.no_expiry ?? data.filter((quiz) => !quiz.expire_date).length;
  const totalQuizzes = totals?.total_quizes ?? data.length;
  const quizSolved =
    totals?.quiz_solved ??
    data.filter((quiz) => Number(quiz.attempt) > 0).length;

  const filters = [
    { key: "all", label: "All Quizzes" },
    { key: "cards", label: "Action-cards" },
    { key: "educational", label: "Educational videos" },
    { key: "organization", label: "Organizational specific challenges" },
  ];

  const groupedQuizzes = [
    {
      key: "cards",
      title: "Action-cards",
      quizzes: filteredData.filter((quiz) => quiz.category === "cards"),
    },
    {
      key: "educational",
      title: "Educational videos",
      quizzes: filteredData.filter((quiz) => quiz.category === "educational"),
    },
    {
      key: "organization",
      title: "Organizational specific challenges",
      quizzes: filteredData.filter((quiz) => quiz.category === "organization"),
    },
  ].filter((group) => group.quizzes.length);

  const renderQuizCard = (quiz, index) => {
    const tone = getQuizTone(quiz);
    const questionCount = quiz.questions?.length || 0;

    return (
      <div
        key={quiz.id}
        className={`overflow-hidden rounded-[18px] border border-[#A9D5AF] shadow-sm ${tone.card}`}
      >
        {/* Top Color Border */}
        <div className={`h-[8px] w-full ${tone.top}`} />

        <div className="flex h-full flex-col px-4 py-3">
          {/* Header */}
        <div className="flex justify-end">
  <span
    className={`text-[16px] font-bold uppercase tracking-wide ${tone.text}`}
  >
    {tone.label}
  </span>
</div>

          {/* Title */}
          <h2 className="mt-1 text-[30px] font-bold leading-none text-[#17181F]">
            {quiz.displayName}
          </h2>

          {/* Info */}
          <div className="mt-3 flex flex-row  gap-x-12 gap-y-4 text-[18px]  text-[#5D6584]">
            <div className="flex items-center gap-2">
              <FaQuestionCircle size={20} className="text-[#5D6584]" />
              <span>
                {questionCount} {questionCount === 1 ? "Question" : "Questions"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <FaCalendarAlt size={20} className="text-[#5D6584]" />

              <span>{quiz.expire_date || "No Expiry"}</span>
            </div>
          </div>

          {/* Button */}
          <Button
            type="button"
            onClick={() => handleStart(quiz)}
            disabled={activeQuizId === quiz.id}
            variant="contained"
            startIcon={activeQuizId === quiz.id ? null : <FaPlay size={13} />}
            sx={{
              alignSelf: "center",
              mt: { xs: 2.5, sm: 4 },
              width: "92%",
              minHeight: { xs: 40, sm: 44 },
              borderRadius: "999px",
              backgroundColor: "#FF5E5B",
              color: "#fff",
              fontSize: { xs: "0.875rem", sm: "1.125rem" },
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: "#F64B48" },
              "&:disabled": { opacity: 0.7 },
            }}
          >
            {activeQuizId === quiz.id ? (
              <>
                <span className="spinner-border spinner-border-sm" />
                Starting...
              </>
            ) : (
              quiz.attempt === 0 ? "Start Quiz" : "Re attempt"
            )}
          </Button>
        </div>
      </div>
    );
  };

  const StatCard = ({ icon: Icon, label, value, iconBg, iconColor }) => (
    <article className="grid min-h-[42px] grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-[#E4E7EE] bg-white px-2.5 py-2 shadow-sm sm:flex sm:min-h-[100px] sm:gap-4 sm:rounded-xl sm:p-5">
      <span
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[13px] sm:h-12 sm:w-12 sm:rounded-xl sm:text-xl ${iconBg} ${iconColor}`}
      >
        <Icon aria-hidden="true" />
      </span>
      <div className="min-w-0 sm:block">
        <span className="block truncate text-[11px] font-bold uppercase tracking-wide text-[#576176] sm:text-[11px]">
          {label}
        </span>
      </div>
      <strong className="block text-base font-bold leading-tight text-[#20232A] sm:text-2xl">
        {value}
      </strong>
    </article>
  );
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4">
      <h1 className="mb-4  text-[24px] font-bold leading-tight text-black md:text-[30px]">
        Quiz List
      </h1>
      <section
        aria-label="Quiz summary"
        className="mb-4 rounded-xl border border-[#B8B8B8] bg-white p-4 shadow-sm sm:mb-8 sm:rounded-xl sm:p-6"
      >
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
          <StatCard
            icon={FaListAlt}
            label="Total Quizzes"
            value={pad(totalQuizzes)}
            iconBg="bg-[#FFF0F1]"
            iconColor="text-[#FF4B50]"
          />
          <StatCard
            icon={FaClock}
            label="No Expiry"
            value={pad(noExpiryCount)}
            iconBg="bg-[#EEF6FF]"
            iconColor="text-[#2F80ED]"
          />
          <StatCard
            icon={FaClipboardList}
            label="Total Questions"
            value={totalQuestions}
            iconBg="bg-[#EAFBF1]"
            iconColor="text-[#25C456]"
          />
          <StatCard
            icon={FaCheckCircle}
            label="Quizzes Solved"
            value={pad(quizSolved)}
            iconBg="bg-[#EAFBF1]"
            iconColor="text-[#25C456]"
          />
        </div>
      </section>

      <section className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div
          className="flex flex-wrap items-center gap-2"
          aria-label="Quiz filters"
        >
          {filters.map((filter) => (
            <Button
              key={filter.key}
              type="button"
              onClick={() => setActiveFilter(filter.key)}
              variant={activeFilter === filter.key ? "contained" : "outlined"}
              sx={{
                borderRadius: "8px",
                flexShrink: 0,
                fontSize: "0.75rem",
                fontWeight: 700,
                minHeight: 36,
                px: 2,
                textTransform: "none",
                ...(activeFilter === filter.key
                  ? {
                      backgroundColor:
                        filter.key === "cards"
                          ? "#25C456"
                          : filter.key === "educational"
                            ? "#3F8FE5"
                            : "#FF5A5F",
                      borderColor:
                        filter.key === "cards"
                          ? "#25C456"
                          : filter.key === "educational"
                            ? "#3F8FE5"
                            : "#FF5A5F",
                      color: "#fff",
                    }
                  : {
                      backgroundColor: "#fff",
                      borderColor:
                        filter.key === "cards"
                          ? "#25C456"
                          : filter.key === "educational"
                            ? "#3F8FE5"
                            : "#FF5A5F",
                      color:
                        filter.key === "cards"
                          ? "#1F9D4A"
                          : filter.key === "educational"
                            ? "#2B83D8"
                            : "#FF4B50",
                    }),
              }}
            >
              {filter.label}
            </Button>
          ))}
        </div>

        <div
          className="flex min-h-[38px] w-full items-center gap-2 rounded-[8px] border border-[#D0D5DD] bg-white px-3 lg:w-[280px]"
          role="search"
        >
          <FaSearch className="shrink-0 text-[#7B8496]" aria-hidden="true" />
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <input
              type="text"
              placeholder="Search quiz"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent text-[14px] outline-none placeholder:text-[#7B8496]"
            />
            {search && (
              <Button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear quiz search"
                variant="text"
                sx={{
                  flexShrink: 0,
                  minHeight: 26,
                  minWidth: 0,
                  borderRadius: "999px",
                  backgroundColor: "#F2F4F7",
                  color: "#576176",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  px: 1,
                  py: 0.5,
                  textTransform: "none",
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="rounded-[8px] border border-[#E4E7EE] bg-white p-8 text-center text-[14px] font-semibold text-[#667085] shadow-sm">
          Loading quizzes...
        </div>
      ) : filteredData.length === 0 ? (
        <div className="rounded-[8px] border border-[#E4E7EE] bg-white p-8 text-center text-[14px] font-semibold text-[#667085] shadow-sm">
          Data Not Found
        </div>
      ) : (
        <section className="rounded-[8px] border border-[#B8B8B8] bg-white p-4 shadow-sm sm:p-6">
          {activeFilter === "all" ? (
            groupedQuizzes.map((group) => (
              <div className="mb-10 last:mb-0" key={group.key}>
                <div className="mb-8 flex items-center gap-2">
                  <span className="h-8 w-1 rounded-full bg-[#FF5A5F]"></span>

                  <h2 className="m-0 text-[24px] font-extrabold leading-none text-[#20232A]">
                    {group.title}
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {group.quizzes.map((quiz, index) =>
                    renderQuizCard(quiz, index),
                  )}
                </div>
              </div>
            ))
          ) : (
            <div>
              <div className="mb-8 flex items-center gap-2">
                <span className="h-8 w-1 rounded-full bg-[#FF5A5F]"></span>

                <h2 className="m-0 text-[24px] font-extrabold leading-none text-[#20232A]">
                  {filters.find((filter) => filter.key === activeFilter)?.label}
                </h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                {filteredData.map((quiz, index) => renderQuizCard(quiz, index))}
              </div>
            </div>
          )}
        </section>
      )}

      {showModal && (
        <div className="modal d-block bg-black/40" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content overflow-hidden rounded-[8px] border-0 shadow-2xl">
              <div className="modal-header">
                <h5 className="modal-title">Reattempt Quiz</h5>
                <IconButton
                  type="button"
                  onClick={closeModal}
                  disabled={isModalLoading}
                  aria-label="Close"
                  size="small"
                >
                  <span aria-hidden="true">&times;</span>
                </IconButton>
              </div>

              <div className="modal-body text-center">
                <p>
                  Are you sure you want to reattempt this quiz? You have{" "}
                  {3 - selectedQuiz.attempt} reattempt remaining.
                </p>
              </div>

              <div className="modal-footer justify-content-center">
                <Button
                  variant="outlined"
                  onClick={closeModal}
                  disabled={isModalLoading}
                  sx={{ minWidth: 92, textTransform: "none" }}
                >
                  No
                </Button>

                <Button
                  variant="contained"
                  onClick={handleYes}
                  disabled={isModalLoading}
                  sx={{ minWidth: 92, textTransform: "none" }}
                >
                  {isModalLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Processing...
                    </>
                  ) : (
                    "Yes"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizList;
